<!-- src/views/new-tip-v2/AssignmentsManager.vue -->
<template>
  <v-container fluid>
    <div class="d-flex align-center mb-4">
      <v-btn icon :to="{ name: 'new-tip-v2.campaigns.show', params: { id } }">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <div class="ml-2">
        <h1 class="text-h5 font-weight-medium">Affectations</h1>
        <span v-if="campaign" class="text-caption grey--text">
          Campagne : {{ campaign.name }} ·
          {{ formatDate(campaign.start_date) }} → {{ formatDate(campaign.end_date) }}
          <template v-if="productName"> · Produit : {{ productName }}</template>
        </span>
      </div>
      <v-spacer />
      <v-btn outlined @click="openImport">
        <v-icon left>mdi-file-excel</v-icon>
        Import Excel
      </v-btn>
      <v-btn color="primary" class="ml-2" :loading="saving" :disabled="!tiersValid" @click="save">
        <v-icon left>mdi-content-save</v-icon>
        Sauvegarder
      </v-btn>
    </div>

    <v-alert v-if="dirty" type="info" outlined dense class="mb-3">
      Des modifications non sauvegardées sont en attente.
    </v-alert>

    <v-card outlined :loading="loading">
      <v-card-text>
        <new-tip-v2-tiers-table
          v-model="rows"
          :supervisors="supervisors"
          :known-salepoints="knownSalepoints"
          @row-delete="onRowDelete"
          @validity="tiersValid = $event"
        />
      </v-card-text>
    </v-card>

    <!-- Dialog Import Excel -->
    <v-dialog v-model="importDialog" max-width="640">
      <v-card>
        <v-card-title>Import Excel</v-card-title>
        <v-card-text>
          <p class="text-body-2 grey--text mb-2">
            Colonnes attendues (ordre libre, casse indifférente) :
          </p>
          <ul class="text-caption grey--text mb-3">
            <li><code>pos_code</code> — obligatoire</li>
            <li><code>supervisor_id</code> ou <code>supervisor_phone</code> — au moins l'un des deux</li>
            <li><code>objective_1_qty</code>, <code>objective_1_gain</code> — obligatoires (palier 1)</li>
            <li><code>objective_2_qty</code>, <code>objective_2_gain</code> — facultatifs</li>
            <li><code>objective_3_qty</code>, <code>objective_3_gain</code> — facultatifs</li>
            <li><code>hats_gain</code> — facultatif</li>
          </ul>
          <v-alert type="info" dense text class="mb-3">
            Le produit n'est pas dans le fichier : il est porté par la campagne.
            Les quantités et les gains doivent croître du palier 1 au palier 3,
            sans sauter de palier — les lignes non conformes sont rejetées et
            listées dans le rapport.
          </v-alert>

          <v-file-input
            v-model="importFile" label="Fichier Excel"
            accept=".xlsx,.xls,.csv" prepend-icon="mdi-file-excel"
            outlined dense show-size
          />

          <v-alert
            v-if="importReport"
            :type="importReport.errors.length ? 'warning' : 'success'"
            outlined dense
          >
            <div>
              {{ importReport.summary.created }} créées ·
              {{ importReport.summary.updated }} mises à jour ·
              {{ importReport.summary.skipped }} ignorées
            </div>
            <ul v-if="importReport.errors.length" class="mt-2 mb-0">
              <li
                v-for="(err, i) in importReport.errors.slice(0, 15)"
                :key="i" class="text-caption"
              >
                Ligne {{ err.row || err.index }} : {{ err.message }}
              </li>
            </ul>
            <div
              v-if="importReport.errors.length > 15"
              class="text-caption mt-1"
            >
              … et {{ importReport.errors.length - 15 }} autre(s).
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeImport">Fermer</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!importFile" @click="runImport">
            Importer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { formatDate } from '@/helpers/formatters';
import TiersTable from '@/components/new-tip-v2/TiersTable.vue';
import api from '@/api/newTipV2Api';

export default {
  name: 'NewTipV2AssignmentsManager',
  components: { 'new-tip-v2-tiers-table': TiersTable },
  props: { id: { type: [String, Number], required: true } },
  data() {
    return {
      rows: [],
      originalRows: [],
      supervisors: [],
      // POS déjà rattachés : leurs code+nom viennent de la relation chargée
      // par l'API, ce qui évite une recherche pour les afficher.
      knownSalepoints: [],
      tiersValid: true,
      importDialog: false,
      importFile: null,
      importReport: null,
      pendingDeletions: [],
    };
  },
  computed: {
    ...mapState('newTipV2', {
      campaign: s => s.currentCampaign,
      assignments: s => s.assignments,
    }),
    loading() { return this.$store.getters['newTipV2/isLoading']('assignments'); },
    saving() { return this.$store.getters['newTipV2/isLoading']('saving'); },
    productName() {
      const p = this.campaign && this.campaign.product;
      return p ? (p.name || '').trim() : null;
    },
    dirty() {
      return JSON.stringify(this.rows) !== JSON.stringify(this.originalRows)
          || this.pendingDeletions.length > 0;
    },
  },
  methods: {
    ...mapActions('newTipV2', [
      'fetchAssignments', 'saveAssignments', 'importAssignments', 'deleteAssignment', 'fetchCampaign',
    ]),
    formatDate,

    async load() {
      await this.fetchAssignments({ campaignId: this.id });

      this.rows = this.assignments.map(a => ({
        id: a.id,
        salepoint_id: a.salepoint_id,
        supervisor_id: a.supervisor_id,
        objective_1_qty: a.objective_1_qty,
        objective_1_gain: a.objective_1_gain,
        objective_2_qty: a.objective_2_qty,
        objective_2_gain: a.objective_2_gain,
        objective_3_qty: a.objective_3_qty,
        objective_3_gain: a.objective_3_gain,
        hats_objective_qty: a.hats_objective_qty,
        hats_gain: a.hats_gain,
        visits_count: a.visits_count,
      }));
      this.originalRows = JSON.parse(JSON.stringify(this.rows));

      this.knownSalepoints = this.assignments.map(a => a.salepoint).filter(Boolean);
    },

    async fetchSupervisors() {
      try {
        const { data } = await api.listSupervisors();
        this.supervisors = (data && data.data) || [];
      } catch (err) {
        console.error('Erreur de chargement supervisors', err);
      }
    },

    onRowDelete(assignmentId) {
      if (assignmentId && !this.pendingDeletions.includes(assignmentId)) {
        this.pendingDeletions.push(assignmentId);
      }
    },

    async save() {
      for (const assignmentId of this.pendingDeletions) {
        try {
          await this.deleteAssignment(assignmentId);
        } catch (err) {
          console.error(`Échec suppression #${assignmentId}`, err);
        }
      }
      this.pendingDeletions = [];

      const valid = this.rows.filter(r => r.salepoint_id && r.supervisor_id);
      if (valid.length === 0) {
        this.$emit('toast', 'Aucune affectation à enregistrer');
        await this.load();
        return;
      }

      try {
        const result = await this.saveAssignments({ campaignId: this.id, assignments: valid });
        this.$emit('toast', `${result.summary.created} créées, ${result.summary.updated} mises à jour, ${result.summary.skipped} ignorées`);
      } catch (err) {
        // 422 de validation : on remonte les messages de paliers refusés.
        const errors = err.response && err.response.data && err.response.data.errors;
        if (errors) {
          this.$emit('toast', Object.values(errors).flat().join(' '));
        }
        console.error(err);
      }
      await this.load();
    },

    openImport() {
      this.importDialog = true;
      this.importReport = null;
      this.importFile = null;
    },
    closeImport() { this.importDialog = false; },

    async runImport() {
      if (!this.importFile) return;
      this.importReport = await this.importAssignments({
        campaignId: this.id,
        file: this.importFile,
      });
      await this.load();
    },
  },

  async mounted() {
    await Promise.all([this.fetchSupervisors(), this.load()]);
  },
};
</script>
